import { type ReactNode } from "react";
import { clsx } from "clsx";

// ---- Date range presets ------------------------------------------------

export type RangePreset = "30d" | "90d" | "qtd" | "ytd" | "all";

export const RANGE_PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "qtd", label: "QTD" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All" },
];

// Returns ISO date strings (YYYY-MM-DD) for the inclusive bounds of the
// selected preset. The "all" preset uses a far-past start so every row
// passes the filter — simpler than branching on `preset === "all"` at every
// call site.
export function computeRange(
  preset: RangePreset,
  now: Date = new Date(),
): { startIso: string; endIso: string } {
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  const endIso = toIsoDate(end);

  if (preset === "all") {
    return { startIso: "1970-01-01", endIso };
  }

  const start = new Date(end);
  if (preset === "30d") {
    start.setDate(start.getDate() - 29);
  } else if (preset === "90d") {
    start.setDate(start.getDate() - 89);
  } else if (preset === "qtd") {
    const quarterStartMonth = Math.floor(start.getMonth() / 3) * 3;
    start.setMonth(quarterStartMonth, 1);
  } else if (preset === "ytd") {
    start.setMonth(0, 1);
  }

  return { startIso: toIsoDate(start), endIso };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ---- Formatters --------------------------------------------------------

export function formatCents(cents: number, currency = "USD"): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  return formatter.format(cents / 100);
}

// ---- UI primitives -----------------------------------------------------

export function RangePicker({
  value,
  onChange,
}: {
  value: RangePreset;
  onChange: (next: RangePreset) => void;
}) {
  return (
    <div
      role="group"
      data-testid="reports-range-picker"
      className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 shadow-sm"
    >
      {RANGE_PRESETS.map((p) => {
        const active = p.value === value;
        return (
          <button
            key={p.value}
            type="button"
            data-testid={`reports-range-${p.value}`}
            aria-pressed={active}
            onClick={() => onChange(p.value)}
            className={clsx(
              "px-3 py-1 text-xs font-medium rounded-[5px] transition-colors",
              active
                ? "bg-[#0070F2] text-white"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export function Card({
  title,
  action,
  children,
  className,
  testId,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={clsx(
        "bg-white rounded-md border border-slate-200 shadow-sm p-4",
        className,
      )}
    >
      {(title || action) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          {title && (
            <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// Centered empty state used by every chart tile.
export function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-[240px] flex items-center justify-center text-sm text-slate-500">
      {message}
    </div>
  );
}

// Filter a date string (YYYY-MM-DD or full ISO) against inclusive bounds.
// All report consumers call this once per row, so keep it cheap.
export function withinRange(
  iso: string | null | undefined,
  startIso: string,
  endIso: string,
): boolean {
  if (!iso) return false;
  const day = iso.slice(0, 10);
  return day >= startIso && day <= endIso;
}
